import React, { useMemo } from 'react';
import Emails from './Emails';
import Advanced from './Advanced';
import birthdayIcon from '/img/icons/birthday-no-bg.svg';
import calendarIcon from '/img/contact/icons/source/calendar.svg';
import reconnectIcon from '/img/feed/reconnect-icon.svg';
import {
  Section,
  SettingToggle,
  SettingsBoxesWrapper,
  SettingsSectionBox,
  SettingsSectionLabel,
  SettingsSectionRow,
} from '@views/Settings/components';
import { Badge } from '@repo/components/badges';
import { NewContactIcon } from '@repo/icons';
import styles from './Notifications.module.scss';
import useEnabledNotifications from './use-enabled-notifications';
import { NotificationType } from '@repo/utils/types';
import SystemSettings from './SystemSettings';

const pushNotificationItems: {
  label: string;
  notification: NotificationType;
  Icon: () => JSX.Element;
  description?: string;
  desktopOnly: boolean;
}[] = [
  {
    label: 'Birthdays',
    notification: 'birthdays',
    Icon: () => <Badge src={birthdayIcon} />,
    desktopOnly: true,
  },
  {
    label: 'Pre-meeting',
    notification: 'pre_meeting',
    Icon: () => <Badge src={calendarIcon} />,
    description: 'Receive a reminder to prepare for your next meeting 15 minutes in advance.',
    desktopOnly: true,
  },
  {
    label: 'In-meeting',
    notification: 'in_meeting',
    Icon: () => <Badge src={calendarIcon} />,
    description: 'Receive a reminder to take notes during your meeting.',
    desktopOnly: true,
  },
  {
    label: 'Post-meeting',
    notification: 'post_meeting',
    Icon: () => <Badge src={calendarIcon} />,
    description: 'Receive a reminder to take notes after your meeting has ended.',
    desktopOnly: true,
  },
  {
    label: 'New Clay Members',
    notification: 'clay_contact_user',
    Icon: () => <NewContactIcon className={undefined} />,
    desktopOnly: true,
  },
  {
    label: 'Reconnect',
    notification: 'reconnects',
    Icon: () => <Badge src={reconnectIcon} />,
    desktopOnly: true,
  },
];

function PushNotifications() {
  const { enabledNotifications, toggleNotification } = useEnabledNotifications();
  const isElectron = window.app.isElectron;
  const filteredNotificationItems = useMemo(() => {
    if (isElectron) {
      return pushNotificationItems;
    } else {
      return pushNotificationItems.filter(({ desktopOnly }) => !desktopOnly);
    }
  }, []);

  if (filteredNotificationItems.length === 0) {
    return null;
  }

  return (
    <Section>
      <SettingsSectionLabel>Push Notifications</SettingsSectionLabel>
      <SettingsBoxesWrapper>
        <SystemSettings />
        <SettingsSectionBox>
          {filteredNotificationItems.map(({ label, Icon, notification, description }) => {
            return (
              <SettingsSectionRow className={styles.pushNotification}>
                <div className={styles.toggle}>
                  <Icon />
                  <SettingToggle
                    title={label}
                    checked={enabledNotifications[notification]}
                    name={notification}
                    onChange={() => toggleNotification(notification)}
                  />
                </div>
                {description && <p>{description}</p>}
              </SettingsSectionRow>
            );
          })}
        </SettingsSectionBox>
      </SettingsBoxesWrapper>
    </Section>
  );
}

export default function NotificationsSettingsPanel() {
  return (
    <>
      <PushNotifications />
      <Emails />
      <Advanced />
    </>
  );
}
